import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getAllModules, moduleSupportsDetailPages, getModuleServiceType } from '../utils/memoryPalaceUtils';
import { bookService } from '../services/bookService';
import { peopleService } from '../services/peopleService';
import { memoryService } from '../services/memoryService';
import { NovelService } from '../services/novelService';
import { NotesService } from '../services/notesService';
import { KnowledgeCategoryService } from '../services/knowledgeCategoryService';
import { getWhiteboards } from '../services/whiteboardService';
import { taskService, TaskFormData } from '../services/taskService';
import TaskForm from './tasks/TaskForm';
import CashflowForm from './cashflow/CashflowForm';
import './QuickCommand.css';

interface CommandSuggestion {
  type: 'goto' | 'goto-detail' | 'create' | 'history';
  module: { id: string; label: string; path: string };
  itemId?: string;
  displayText: string;
  description?: string;
  path?: string;
}

type ViewMode = 'action' | 'history' | 'modules';

interface HistoryEntry {
  path: string;
  label: string;
  title?: string; // Page title from document.title
  timestamp: number;
}

const MODULES_WITH_CREATE = ['tasks', 'memory', 'cashflow'];
const HISTORY_STORAGE_KEY = 'quickCommandHistory';
const MAX_HISTORY_ENTRIES = 20;

const QuickCommand: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<CommandSuggestion[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [hasMoved, setHasMoved] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('modules');
  const dragStartPosRef = useRef({ x: 0, y: 0 });
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  
  // Task creation modal state
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);
  const [taskError, setTaskError] = useState<string | null>(null);
  const [taskSuccess, setTaskSuccess] = useState<string | null>(null);

  // Cashflow creation modal state
  const [showCashflowModal, setShowCashflowModal] = useState(false);

  // Get modules once on mount
  const allModules = React.useMemo(() => getAllModules(), []);

  // Helper functions for history
  const getHistory = React.useCallback((): HistoryEntry[] => {
    try {
      const stored = localStorage.getItem(HISTORY_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error reading history:', error);
      return [];
    }
  }, []);

  const saveHistory = React.useCallback((history: HistoryEntry[]) => {
    try {
      localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(history));
    } catch (error) {
      console.error('Error saving history:', error);
    }
  }, []);

  // Track navigation history
  useEffect(() => {
    const currentPath = location.pathname;
    if (currentPath === '/') return; // Don't track home page

    const history = getHistory();
    const existingIndex = history.findIndex(entry => entry.path === currentPath);
    
    // Remove if exists to avoid duplicates
    if (existingIndex !== -1) {
      history.splice(existingIndex, 1);
    }

    // Get module label for current path
    const module = allModules.find(m => currentPath.startsWith(m.path));
    const label = module ? module.label : currentPath.split('/').pop() || currentPath;

    // Capture page title from document.title with a delay to allow components to set their titles
    // First, add entry without title
    const newEntry: HistoryEntry = {
      path: currentPath,
      label: label,
      timestamp: Date.now()
    };

    // Add to beginning
    history.unshift(newEntry);

    // Keep only last MAX_HISTORY_ENTRIES
    const trimmedHistory = history.slice(0, MAX_HISTORY_ENTRIES);
    saveHistory(trimmedHistory);

    // Update title after a delay to allow components to set document.title
    setTimeout(() => {
      const pageTitle = document.title || undefined;
      if (pageTitle && pageTitle !== 'Memory Land') {
        const updatedHistory = getHistory();
        const entryIndex = updatedHistory.findIndex(entry => entry.path === currentPath);
        if (entryIndex !== -1) {
          updatedHistory[entryIndex].title = pageTitle;
          saveHistory(updatedHistory);
        }
      }
    }, 100); // 100ms delay to allow components to set their titles
  }, [location.pathname, allModules, getHistory, saveHistory]);

  // Load saved position from localStorage on mount
  useEffect(() => {
    const savedPosition = localStorage.getItem('quickCommandButtonPosition');
    if (savedPosition) {
      try {
        const { x, y } = JSON.parse(savedPosition);
        setPosition({ x, y });
      } catch (error) {
        console.error('Error loading saved position:', error);
      }
    } else {
      // Default position (bottom right)
      const defaultX = window.innerWidth - 3.5 * 16 - 1 * 16; // button width + right margin
      const defaultY = window.innerHeight - 3.5 * 16 - 1 * 16; // button height + bottom margin
      setPosition({ x: defaultX, y: defaultY });
    }
  }, []);

  // Save position to localStorage whenever it changes
  useEffect(() => {
    if (position.x !== 0 || position.y !== 0) {
      localStorage.setItem('quickCommandButtonPosition', JSON.stringify(position));
    }
  }, [position]);

  // Handle window resize to keep button within bounds
  useEffect(() => {
    const handleResize = () => {
      const maxX = window.innerWidth - 3.5 * 16; // button width
      const maxY = window.innerHeight - 3.5 * 16; // button height
      
      setPosition(prev => ({
        x: Math.max(0, Math.min(prev.x, maxX)),
        y: Math.max(0, Math.min(prev.y, maxY))
      }));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle drag start (mouse)
  const handleMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left - rect.width / 2,
        y: e.clientY - rect.top - rect.height / 2
      });
      dragStartPosRef.current = { x: position.x, y: position.y };
      setIsDragging(true);
      setHasMoved(false);
    }
  };

  // Handle drag start (touch)
  const handleTouchStart = (e: React.TouchEvent<HTMLButtonElement>) => {
    if (buttonRef.current && e.touches.length === 1) {
      const touch = e.touches[0];
      const rect = buttonRef.current.getBoundingClientRect();
      setDragOffset({
        x: touch.clientX - rect.left - rect.width / 2,
        y: touch.clientY - rect.top - rect.height / 2
      });
      dragStartPosRef.current = { x: position.x, y: position.y };
      setIsDragging(true);
      setHasMoved(false);
      // Prevent default to avoid scrolling while dragging
      e.preventDefault();
    }
  };

  // Handle drag (both mouse and touch)
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      
      // Check if mouse has moved significantly (more than 5px) from start position
      const moved = Math.abs(newX - dragStartPosRef.current.x) > 5 || 
                    Math.abs(newY - dragStartPosRef.current.y) > 5;
      if (moved) {
        setHasMoved(true);
      }
      
      // Constrain to viewport
      const maxX = window.innerWidth - 3.5 * 16; // button width
      const maxY = window.innerHeight - 3.5 * 16; // button height
      
      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      });
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        const touch = e.touches[0];
        const newX = touch.clientX - dragOffset.x;
        const newY = touch.clientY - dragOffset.y;
        
        // Check if touch has moved significantly (more than 5px) from start position
        const moved = Math.abs(newX - dragStartPosRef.current.x) > 5 || 
                      Math.abs(newY - dragStartPosRef.current.y) > 5;
        if (moved) {
          setHasMoved(true);
          // Prevent scrolling while dragging
          e.preventDefault();
        }
        
        // Constrain to viewport
        const maxX = window.innerWidth - 3.5 * 16; // button width
        const maxY = window.innerHeight - 3.5 * 16; // button height
        
        setPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY))
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      // Reset hasMoved after a short delay to allow onClick to check it
      // Shorter delay for mouse since click events fire faster
      setTimeout(() => setHasMoved(false), 50);
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
      // Reset hasMoved after a delay to prevent onClick from firing after drag
      // This prevents the modal from opening when user finishes dragging on mobile
      setTimeout(() => setHasMoved(false), 300);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);
    window.addEventListener('touchcancel', handleTouchEnd);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [isDragging, dragOffset]);

  // Fetch items for a specific module
  const fetchModuleItems = async (modulePath: string, searchTerm: string = '') => {
    if (!user) return [];
    
    setLoading(true);
    try {
      const serviceType = getModuleServiceType(modulePath);
      let items: any[] = [];

      let needsClientSideFiltering = true;
      
      switch (serviceType) {
        case 'book':
          if (searchTerm) {
            const books = await bookService.searchBooks(searchTerm);
            items = books || [];
            needsClientSideFiltering = false; // Already filtered server-side
          } else {
            const books = await bookService.getBooks();
            items = books || [];
          }
          break;
        
        case 'people':
          const peopleResponse = await peopleService.getPeopleByUserId(user.id, searchTerm);
          items = peopleResponse.success && peopleResponse.people ? peopleResponse.people : [];
          needsClientSideFiltering = false; // Already filtered server-side
          break;
        
        case 'novel':
          const novels = await NovelService.getUserNovels(user.id);
          items = novels || [];
          break;
        
        case 'notes':
          const notesResponse = await NotesService.getUserNotes(user.id);
          items = notesResponse.success && notesResponse.data ? notesResponse.data : [];
          break;
        
        case 'knowledge':
          const categoriesResponse = await KnowledgeCategoryService.getCategories();
          items = categoriesResponse.categories || [];
          break;
        
        case 'whiteboard':
          const whiteboards = await getWhiteboards();
          items = whiteboards || [];
          break;
        
        case 'memory':
          const placesResponse = await memoryService.getUserPlaces(user.id);
          console.log('Memory places response:', placesResponse);
          items = placesResponse.success && placesResponse.places ? placesResponse.places : [];
          console.log('Memory places items:', items);
          break;

        default:
          break;
      }

      // Filter items based on search term (only if not already filtered server-side)
      if (searchTerm && needsClientSideFiltering) {
        const lowerSearch = searchTerm.toLowerCase();
        items = items.filter((item: any) => {
          const title = item.title || item.name || '';
          return title.toLowerCase().includes(lowerSearch);
        });
      }

      return items.slice(0, 10); // Limit to 10 items
    } catch (error) {
      console.error('Error fetching module items:', error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Parse command and generate suggestions based on view mode
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setSuggestions([]);
      setSelectedIndex(0);
      return;
    }

    const query = searchQuery.trim().toLowerCase();
    
    if (viewMode === 'action') {
      // Action mode: show create commands
      const createModules = allModules.filter(m => MODULES_WITH_CREATE.includes(m.id));
      
      if (!query) {
        // Show all create options
        const actionSuggestions: CommandSuggestion[] = createModules.map(module => ({
          type: 'create',
          module,
          displayText: `create ${module.path}`,
          description: `Create new item in ${module.label}`
        }));
        setSuggestions(actionSuggestions);
        return;
      }

      // Filter by search query
      const filtered = createModules
        .filter(module => {
          const moduleName = module.path.replace('/', '').toLowerCase();
          const moduleLabel = module.label.toLowerCase();
          const moduleId = module.id.toLowerCase();
          return moduleName.includes(query) || 
                 moduleLabel.includes(query) || 
                 moduleId.includes(query);
        })
        .map(module => ({
          type: 'create' as const,
          module,
          displayText: `create ${module.path}`,
          description: `Create new item in ${module.label}`
        }));
      
      setSuggestions(filtered);
      return;
    }

    if (viewMode === 'history') {
      // History mode: show recently visited pages
      const history = getHistory();
      
      if (!query) {
        // Show all history entries
        const historySuggestions: CommandSuggestion[] = history.map(entry => {
          // Find matching module for better display
          const module = allModules.find(m => entry.path.startsWith(m.path)) || allModules[0];
          // Use title if available, otherwise use label, fallback to path
          const displayTitle = entry.title || entry.label;
          return {
            type: 'history' as const,
            module,
            displayText: displayTitle,
            description: entry.path,
            path: entry.path
          };
        });
        setSuggestions(historySuggestions);
        return;
      }

      // Filter history by search query
      const filtered = history
        .filter(entry => {
          const searchableText = [
            entry.path,
            entry.label,
            entry.title
          ].filter(Boolean).join(' ').toLowerCase();
          return searchableText.includes(query);
        })
        .map(entry => {
          const module = allModules.find(m => entry.path.startsWith(m.path)) || allModules[0];
          // Use title if available, otherwise use label, fallback to path
          const displayTitle = entry.title || entry.label;
          return {
            type: 'history' as const,
            module,
            displayText: displayTitle,
            description: entry.path,
            path: entry.path
          };
        });
      
      setSuggestions(filtered);
      return;
    }

    // Modules mode: existing logic for navigation
    if (!query) {
      // Show default suggestions when empty
      const defaultSuggestions: CommandSuggestion[] = [];
      
      // Add goto suggestions for popular modules
      allModules.slice(0, 8).forEach(module => {
        defaultSuggestions.push({
          type: 'goto',
          module,
          displayText: `goto ${module.path}`,
          description: 'Navigate to module'
        });
      });

      setSuggestions(defaultSuggestions);
      return;
    }

    const suggestions: CommandSuggestion[] = [];
    const queryLower = query.toLowerCase();

    // Parse "goto /module_name" commands
    if (queryLower.startsWith('goto ')) {
      const rest = query.slice(5).trim();
      
      if (!rest || rest === '/') {
        // Show "goto /" to trigger module list
        suggestions.push({
          type: 'goto',
          module: allModules[0], // dummy
          displayText: 'goto /',
          description: 'Type module name to navigate'
        });
        setSuggestions(suggestions);
        return;
      }

      const parts = rest.split(/\s+/);
      const moduleQuery = parts[0].replace('/', '').toLowerCase();
      const itemSearchTerm = parts.slice(1).join(' '); // Support multi-word names

      // Find matching modules
      let matchingModules = allModules.filter(module => {
        const moduleName = module.path.replace('/', '').toLowerCase();
        const moduleLabel = module.label.toLowerCase();
        const moduleId = module.id.toLowerCase();
        return moduleName.includes(moduleQuery) || 
               moduleLabel.includes(moduleQuery) || 
               moduleId.includes(moduleQuery);
      });
      
      // If there's an exact match, prioritize it for item fetching
      const exactMatch = matchingModules.find(module => {
        const moduleName = module.path.replace('/', '').toLowerCase();
        return moduleName === moduleQuery;
      });
      
      // If we have an exact match and it supports detail pages, treat it as a single match
      if (exactMatch && moduleSupportsDetailPages(exactMatch.path)) {
        matchingModules = [exactMatch];
      }

      if (matchingModules.length === 1 && !itemSearchTerm && moduleSupportsDetailPages(matchingModules[0].path)) {
        // Single module match - fetch and show items
        const module = matchingModules[0];
        
        fetchModuleItems(module.path, itemSearchTerm).then(items => {
          const itemSuggestions: CommandSuggestion[] = [];
          const moduleServiceType = getModuleServiceType(module.path);
          
          // Add base navigation
          itemSuggestions.push({
            type: 'goto',
            module,
            displayText: `goto ${module.path}`,
            description: `Navigate to ${module.label}`
          });

          // Add item suggestions
          items.forEach((item: any) => {
            const itemTitle = item.title || item.name || '';
            // For novels, prioritize novel_id over id (id is numeric, novel_id is string)
            // For memory, use id field
            const itemId = moduleServiceType === 'novel' 
              ? (item.novel_id || item.id || '')
              : moduleServiceType === 'memory'
              ? (item.id || '')
              : (item.id || item.book_id || item.novel_id || item.note_id || item.category_id || '');
            
            itemSuggestions.push({
              type: 'goto-detail',
              module,
              itemId: itemId.toString(),
              displayText: `goto ${module.path} ${itemTitle}`,
              description: `Navigate to "${itemTitle}"`
            });
          });

          setSuggestions(itemSuggestions);
          setSelectedIndex(0);
        });
        
        // Show loading state
        suggestions.push({
          type: 'goto',
          module,
          displayText: `goto ${module.path}`,
          description: 'Loading items...'
        });
        setSuggestions(suggestions);
        return;
      } else if (matchingModules.length === 1 && itemSearchTerm) {
        // User is typing item name - fetch and filter
        const module = matchingModules[0];
        
        if (moduleSupportsDetailPages(module.path)) {
          fetchModuleItems(module.path, itemSearchTerm).then(items => {
            const itemSuggestions: CommandSuggestion[] = [];
            const moduleServiceType = getModuleServiceType(module.path);
            
            // Add filtered item suggestions
            items.forEach((item: any) => {
              const itemTitle = item.title || item.name || '';
              // For novels, prioritize novel_id over id (id is numeric, novel_id is string)
              // For memory, use id field
              const itemId = moduleServiceType === 'novel'
                ? (item.novel_id || item.id || '')
                : moduleServiceType === 'memory'
                ? (item.id || '')
                : (item.id || item.book_id || item.novel_id || item.note_id || item.category_id || '');
              
              itemSuggestions.push({
                type: 'goto-detail',
                module,
                itemId: itemId.toString(),
                displayText: `goto ${module.path} ${itemTitle}`,
                description: `Navigate to "${itemTitle}"`
              });
            });

            if (itemSuggestions.length === 0) {
              itemSuggestions.push({
                type: 'goto',
                module,
                displayText: `goto ${module.path}`,
                description: 'No matching items found'
              });
            }

            setSuggestions(itemSuggestions);
            setSelectedIndex(0);
          });
          
          // Show loading state
          suggestions.push({
            type: 'goto',
            module,
            displayText: `goto ${module.path} ${itemSearchTerm}`,
            description: 'Searching...'
          });
          setSuggestions(suggestions);
          return;
        }
      }

      // Multiple module matches or non-detail modules - show module list
      matchingModules.slice(0, 10).forEach(module => {
        suggestions.push({
          type: 'goto',
          module,
          displayText: `goto ${module.path}`,
          description: `Navigate to ${module.label}`
        });
      });
    }
    // Parse "create /module_name" commands
    else if (queryLower.startsWith('create ')) {
      const rest = query.slice(7).trim();
      
      if (!rest || rest === '/') {
        // Show available create commands
        allModules
          .filter(m => MODULES_WITH_CREATE.includes(m.id))
          .forEach(module => {
            suggestions.push({
              type: 'create',
              module,
              displayText: `create ${module.path}`,
              description: `Create new item in ${module.label}`
            });
          });
        return setSuggestions(suggestions);
      }

      const moduleQuery = rest.replace('/', '').toLowerCase();
      
      const matchingModules = allModules
        .filter(module => {
          const moduleName = module.path.replace('/', '').toLowerCase();
          const moduleLabel = module.label.toLowerCase();
          const moduleId = module.id.toLowerCase();
          return (moduleName.includes(moduleQuery) || 
                  moduleLabel.includes(moduleQuery) || 
                  moduleId.includes(moduleQuery)) &&
                 MODULES_WITH_CREATE.includes(module.id);
        })
        .slice(0, 10);

      matchingModules.forEach(module => {
        suggestions.push({
          type: 'create',
          module,
          displayText: `create ${module.path}`,
          description: `Create new item in ${module.label}`
        });
      });
    }
    // General search - show goto and create options
    else {
      const queryLower = query.toLowerCase();
      
      // Goto suggestions
      allModules
        .filter(module => {
          const moduleName = module.path.replace('/', '').toLowerCase();
          const moduleLabel = module.label.toLowerCase();
          const moduleId = module.id.toLowerCase();
          return moduleName.includes(queryLower) || 
                 moduleLabel.includes(queryLower) || 
                 moduleId.includes(queryLower);
        })
        .slice(0, 5)
        .forEach(module => {
          suggestions.push({
            type: 'goto',
            module,
            displayText: `goto ${module.path}`,
            description: `Navigate to ${module.label}`
          });
        });

      // Create suggestions
      allModules
        .filter(module => {
          const moduleName = module.path.replace('/', '').toLowerCase();
          const moduleLabel = module.label.toLowerCase();
          const moduleId = module.id.toLowerCase();
          return (moduleName.includes(queryLower) || 
                  moduleLabel.includes(queryLower) || 
                  moduleId.includes(queryLower)) &&
                 MODULES_WITH_CREATE.includes(module.id);
        })
        .slice(0, 3)
        .forEach(module => {
          suggestions.push({
            type: 'create',
            module,
            displayText: `create ${module.path}`,
            description: `Create new item in ${module.label}`
          });
        });
    }

    setSuggestions(suggestions);
    setSelectedIndex(0);
  }, [searchQuery, isOpen, viewMode, allModules, getHistory]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : 0);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (suggestions[selectedIndex]) {
          handleSelect(suggestions[selectedIndex]);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, suggestions, selectedIndex]);

  // Scroll selected item into view
  useEffect(() => {
    if (resultsRef.current && suggestions.length > 0) {
      const selectedElement = resultsRef.current.children[selectedIndex] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedIndex, suggestions]);

  const handleSelect = (suggestion: CommandSuggestion) => {
    // Don't navigate on placeholder or loading suggestions
    if (suggestion.displayText.includes('[item name]') || 
        suggestion.displayText.includes('[id]') ||
        suggestion.displayText === 'goto /' ||
        suggestion.description?.includes('Loading') ||
        suggestion.description?.includes('Searching') ||
        suggestion.description?.includes('No matching')) {
      return;
    }

    if (suggestion.type === 'create') {
      // For tasks, show modal instead of navigating
      if (suggestion.module.id === 'tasks') {
        setShowTaskModal(true);
        setIsOpen(false);
        setSearchQuery('');
        setTaskError(null);
        setTaskSuccess(null);
      } else if (suggestion.module.id === 'cashflow') {
        // For cashflow, show modal instead of navigating
        setShowCashflowModal(true);
        setIsOpen(false);
        setSearchQuery('');
      } else {
        // Navigate to module with create param for other modules
        navigate(`${suggestion.module.path}?create=true`);
        setIsOpen(false);
        setSearchQuery('');
      }
    } else if (suggestion.type === 'goto') {
      // Navigate to base module page
      navigate(suggestion.module.path);
      setIsOpen(false);
      setSearchQuery('');
    } else if (suggestion.type === 'goto-detail') {
      // Navigate to detail page with item name/ID
      if (suggestion.itemId) {
        navigate(`${suggestion.module.path}/${suggestion.itemId}`);
        setIsOpen(false);
        setSearchQuery('');
      }
    } else if (suggestion.type === 'history') {
      // Navigate to history path
      if (suggestion.path) {
        navigate(suggestion.path);
        setIsOpen(false);
        setSearchQuery('');
      }
    }
  };

  const handleCreateTask = async (taskData: TaskFormData & { tag_names?: string[] }) => {
    if (!user?.id) return;

    setCreatingTask(true);
    setTaskError(null);
    setTaskSuccess(null);

    try {
      const response = await taskService.createTask(user.id.toString(), taskData);
      
      if (response.success) {
        setTaskSuccess('Task created successfully!');
        // Close modal after a short delay
        setTimeout(() => {
          setShowTaskModal(false);
          setTaskSuccess(null);
        }, 1000);
      } else {
        setTaskError(response.message || 'Failed to create task');
      }
    } catch (err) {
      console.error('Error creating task:', err);
      setTaskError('Failed to create task');
    } finally {
      setCreatingTask(false);
    }
  };

  const handleCloseTaskModal = () => {
    setShowTaskModal(false);
    setTaskError(null);
    setTaskSuccess(null);
  };

  const handleCloseCashflowModal = () => {
    setShowCashflowModal(false);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setIsOpen(false);
    }
  };

  return (
    <>
      <button
        ref={buttonRef}
        className={`quick-command-button ${isDragging ? 'dragging' : ''}`}
        onClick={(e) => {
          // Only open if we didn't just drag (moved more than 5px)
          if (!hasMoved && !isDragging) {
            setIsOpen(true);
          }
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        aria-label="Open quick command"
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
          right: 'auto',
          bottom: 'auto'
        }}
      >
        ⌘
      </button>

      {isOpen && (
        <>
          <div 
            className="quick-command-overlay"
            onClick={handleOverlayClick}
          />
          <div className="quick-command-modal">
            <div className="quick-command-header">
              <input
                ref={inputRef}
                type="text"
                className="quick-command-input"
                placeholder={
                  viewMode === 'action' ? 'Search create actions...' :
                  viewMode === 'history' ? 'Search history...' :
                  'Type a command (e.g., goto /books Harry Potter)'
                }
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoComplete="off"
              />
              <button
                className="quick-command-close"
                onClick={() => setIsOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="quick-command-radio-group">
              <label className="quick-command-radio-label">
                <input
                  type="radio"
                  name="viewMode"
                  value="action"
                  checked={viewMode === 'action'}
                  onChange={(e) => {
                    setViewMode('action');
                    setSearchQuery('');
                    setSelectedIndex(0);
                  }}
                />
                <span>Action</span>
              </label>
              <label className="quick-command-radio-label">
                <input
                  type="radio"
                  name="viewMode"
                  value="history"
                  checked={viewMode === 'history'}
                  onChange={(e) => {
                    setViewMode('history');
                    setSearchQuery('');
                    setSelectedIndex(0);
                  }}
                />
                <span>History</span>
              </label>
              <label className="quick-command-radio-label">
                <input
                  type="radio"
                  name="viewMode"
                  value="modules"
                  checked={viewMode === 'modules'}
                  onChange={(e) => {
                    setViewMode('modules');
                    setSearchQuery('');
                    setSelectedIndex(0);
                  }}
                />
                <span>Modules</span>
              </label>
            </div>
            
            {suggestions.length > 0 && (
              <div className="quick-command-results" ref={resultsRef}>
                {suggestions.map((suggestion, index) => (
                  <div
                    key={`${suggestion.type}-${suggestion.module.id}-${suggestion.itemId || suggestion.path || index}-${index}`}
                    className={`quick-command-item ${
                      index === selectedIndex ? 'selected' : ''
                    } ${suggestion.displayText.includes('[') ? 'placeholder' : ''} ${
                      suggestion.description?.includes('Loading') || suggestion.description?.includes('Searching') ? 'loading' : ''
                    }`}
                    onClick={() => handleSelect(suggestion)}
                  >
                    <span className="quick-command-item-text">
                      {suggestion.displayText}
                    </span>
                    <span className="quick-command-item-label">
                      {suggestion.description || suggestion.module.label}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {searchQuery && suggestions.length === 0 && (
              <div className="quick-command-empty">
                No results found
              </div>
            )}

            {!searchQuery && suggestions.length === 0 && viewMode === 'history' && (
              <div className="quick-command-empty">
                No history yet
              </div>
            )}
          </div>
        </>
      )}

      {/* Task Creation Modal */}
      {showTaskModal && (
        <>
          <div 
            className="quick-command-overlay"
            onClick={handleCloseTaskModal}
          />
          <div className="quick-command-task-modal" onClick={(e) => e.stopPropagation()}>
            <div className="quick-command-task-header">
              <h2 className="quick-command-task-title">Create Task</h2>
              <button
                className="quick-command-close"
                onClick={handleCloseTaskModal}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            
            <div className="quick-command-task-content">
              <TaskForm
                onSubmit={handleCreateTask}
                onCancel={handleCloseTaskModal}
                submitButtonText="Create Task"
                cancelButtonText="Cancel"
                errorMessage={taskError}
                successMessage={taskSuccess}
                isLoading={creatingTask}
              />
            </div>
          </div>
        </>
      )}

      {/* Cashflow Creation Modal */}
      {showCashflowModal && (
        <CashflowForm
          user={user}
          entry={null}
          onClose={handleCloseCashflowModal}
        />
      )}
    </>
  );
};

export default QuickCommand;

